import type { VaultEvent, VaultGraph, VaultLink, VaultNode } from '../../shared/types';

interface DemoArea {
  path: string;
  label: string;
  color: string;
  tags: string[];
  notes: string[];
}

const demoAreas: DemoArea[] = [
  {
    path: '00_Atlas',
    label: 'Atlas',
    color: '#7dd3fc',
    tags: ['map', 'structure'],
    notes: [
      'Knowledge Constellation',
      'Navigation Principles',
      'Concept Cartography',
      'Open Questions',
      'Semantic Landmarks',
      'Routes and Tours',
      'Depth Cues',
      'Graph Vocabulary'
    ]
  },
  {
    path: '10_Research',
    label: 'Research',
    color: '#a78bfa',
    tags: ['research', 'signal'],
    notes: [
      'Literature Radar',
      'AI Interface Notes',
      'Spatial Computing',
      'Local First Systems',
      'Cognitive Load',
      'Human Attention',
      'Pattern Library',
      'Insight Pipeline'
    ]
  },
  {
    path: '20_Projects',
    label: 'Projects',
    color: '#34d399',
    tags: ['project', 'active'],
    notes: [
      'Vault Kosmos',
      'Learning Studio',
      'Publishing Engine',
      'Workshop Planner',
      'Knowledge Garden',
      'Presentation Route Alpha',
      'Review Dashboard',
      'Automation Console'
    ]
  },
  {
    path: '30_Sources',
    label: 'Sources',
    color: '#fbbf24',
    tags: ['source', 'reference'],
    notes: [
      'Obsidian Graph',
      'Three Dimensional UI',
      'Local Data Ethics',
      'Graph Layout Notes',
      'WebGL Performance',
      'File Watchers',
      'Navigation Research',
      'Interface Atmosphere'
    ]
  },
  {
    path: '40_Writing',
    label: 'Writing',
    color: '#fb7185',
    tags: ['writing', 'draft'],
    notes: [
      'Public Alpha Story',
      'Demo Walkthrough',
      'Design Notes',
      'Launch Checklist',
      'Field Report',
      'Narrative Arc',
      'Readme Draft',
      'Release Notes'
    ]
  },
  {
    path: '50_People',
    label: 'People',
    color: '#f472b6',
    tags: ['people', 'context'],
    notes: [
      'Research Partners',
      'Workshop Audience',
      'Maintainers',
      'Learners',
      'Editors',
      'Decision Makers',
      'Power Users',
      'Future Contributors'
    ]
  },
  {
    path: '60_Archive',
    label: 'Archive',
    color: '#94a3b8',
    tags: ['archive', 'history'],
    notes: [
      'Prototype Log',
      'Old Layouts',
      'Rejected Ideas',
      'Screenshot Notes',
      'Performance Traces',
      'Branch History',
      'Session Summaries',
      'Release Archive'
    ]
  }
];

const unresolvedTargets = ['Future Knowledge Engine', 'Immersive Presentation Mode', 'Semantic Embeddings'];

const trailerAreas: DemoArea[] = [
  {
    path: '00_Trailer',
    label: 'Trailer Route',
    color: '#7dd3fc',
    tags: ['trailer', 'route'],
    notes: [
      'Your Vault Becomes Visible',
      'From Files To Constellation',
      'Enter The Kosmos',
      'Public Demo Script'
    ]
  },
  {
    path: '10_Structure',
    label: 'Structure',
    color: '#34d399',
    tags: ['structure', 'links'],
    notes: [
      'Clusters And Links',
      'Wiki Paths',
      'Backlinks In Space',
      'Unresolved Ideas'
    ]
  },
  {
    path: '20_Live_Work',
    label: 'Live Work',
    color: '#f4d35e',
    tags: ['timeline', 'live'],
    notes: [
      'Recent Changes Pulse',
      'Timeline Of Work',
      'Genesis Playback',
      'Current Edit Focus'
    ]
  },
  {
    path: '30_Focus',
    label: 'Focus',
    color: '#a78bfa',
    tags: ['focus', 'navigation'],
    notes: [
      'Focus Flights',
      'Depth Mode',
      'Inspector Context',
      'Calm Exploration'
    ]
  },
  {
    path: '40_Privacy',
    label: 'Privacy',
    color: '#60d394',
    tags: ['privacy', 'local-first'],
    notes: [
      'Private Vaults Stay Local',
      'Local Connector',
      'No Telemetry',
      'Demo Data Only'
    ]
  },
  {
    path: '50_Demo',
    label: 'Demo',
    color: '#fb7185',
    tags: ['demo', 'public'],
    notes: [
      'Explore The Static Demo',
      'GitHub Pages Preview',
      'Run Your Own Vault',
      'Public Alpha'
    ]
  }
];

const trailerUnresolvedTargets = ['Semantic Search Layer', 'Presentation Routes', 'Spatial Reading Mode'];

export function createDemoVaultGraph(now = Date.now()): VaultGraph {
  const nodes: VaultNode[] = [];
  const links: VaultLink[] = [];

  nodes.push({
    id: 'folder:.',
    kind: 'folder',
    path: '',
    label: 'Demo Vault',
    area: 'Vault',
    depth: 0,
    tags: [],
    aliases: [],
    color: '#e2e8f0',
    outgoing: 0,
    incoming: 0
  });

  for (const area of demoAreas) {
    nodes.push({
      id: `folder:${area.path}`,
      kind: 'folder',
      path: area.path,
      label: area.label,
      area: area.path,
      depth: 1,
      tags: area.tags,
      aliases: [],
      color: area.color,
      outgoing: 0,
      incoming: 0
    });
    links.push({
      id: `contains:folder:.->folder:${area.path}`,
      source: 'folder:.',
      target: `folder:${area.path}`,
      kind: 'contains'
    });
  }

  let fileIndex = 0;
  const fileIdsByArea = new Map<string, string[]>();
  const allFileIds: string[] = [];

  for (const area of demoAreas) {
    const areaFileIds: string[] = [];
    for (const note of area.notes) {
      const path = `${area.path}/${note}.md`;
      const id = `file:${path}`;
      const createdAt = new Date(now - (demoAreas.length * 8 - fileIndex) * 1000 * 60 * 60 * 36).toISOString();
      const updatedAt = new Date(now - ((fileIndex % 13) + 1) * 1000 * 60 * 18).toISOString();
      nodes.push({
        id,
        kind: 'file',
        path,
        label: note,
        area: area.path,
        depth: 2,
        extension: 'md',
        size: 1600 + fileIndex * 137,
        createdAt,
        updatedAt,
        type: fileIndex % 5 === 0 ? 'hub' : fileIndex % 3 === 0 ? 'note' : 'brief',
        status: fileIndex % 7 === 0 ? 'active' : fileIndex % 4 === 0 ? 'draft' : 'stable',
        priority: fileIndex % 6 === 0 ? 'high' : 'normal',
        tags: [...area.tags, fileIndex % 2 === 0 ? 'demo' : 'linked'],
        aliases: [],
        color: area.color,
        outgoing: 0,
        incoming: 0
      });
      links.push({
        id: `contains:folder:${area.path}->${id}`,
        source: `folder:${area.path}`,
        target: id,
        kind: 'contains'
      });
      areaFileIds.push(id);
      allFileIds.push(id);
      fileIndex += 1;
    }
    fileIdsByArea.set(area.path, areaFileIds);
  }

  for (const target of unresolvedTargets) {
    nodes.push({
      id: `unresolved:${target}`,
      kind: 'unresolved',
      path: target,
      label: target,
      area: 'Unresolved',
      depth: 1,
      tags: ['open'],
      aliases: [],
      color: '#64748b',
      outgoing: 0,
      incoming: 0,
      unresolved: true
    });
  }

  for (const [areaIndex, area] of demoAreas.entries()) {
    const ids = fileIdsByArea.get(area.path) ?? [];
    const nextArea = demoAreas[(areaIndex + 1) % demoAreas.length];
    const nextIds = fileIdsByArea.get(nextArea.path) ?? [];
    const projectHub = fileIdsByArea.get('20_Projects')?.[0];
    const atlasHub = fileIdsByArea.get('00_Atlas')?.[0];

    ids.forEach((id, index) => {
      addLink(links, 'wikilink', id, ids[(index + 1) % ids.length]);
      addLink(links, 'wikilink', id, nextIds[index % nextIds.length]);
      if (index % 2 === 0 && projectHub && id !== projectHub) addLink(links, 'property', id, projectHub, 'initiative');
      if (index % 3 === 0 && atlasHub && id !== atlasHub) addLink(links, 'markdown', id, atlasHub, 'map');
      if (index === 2) addLink(links, 'wikilink', id, `unresolved:${unresolvedTargets[areaIndex % unresolvedTargets.length]}`);
    });
  }

  applyDemoCounts(nodes, links);

  const contentLinks = links.filter((link) => link.kind !== 'contains');
  return {
    nodes,
    links,
    stats: {
      indexedAt: new Date(now).toISOString(),
      durationMs: 42,
      files: allFileIds.length,
      folders: demoAreas.length + 1,
      unresolved: unresolvedTargets.length,
      links: links.length,
      wikilinks: links.filter((link) => link.kind === 'wikilink').length,
      markdownLinks: links.filter((link) => link.kind === 'markdown').length,
      propertyLinks: links.filter((link) => link.kind === 'property').length,
      orphans: nodes.filter(
        (node) =>
          node.kind === 'file' &&
          !contentLinks.some((link) => link.source === node.id || link.target === node.id)
      ).length
    },
    areas: ['Vault', ...demoAreas.map((area) => area.path), 'Unresolved'],
    tags: ['active', 'archive', 'context', 'demo', 'draft', 'linked', 'map', 'project', 'reference', 'research', 'signal', 'source', 'structure', 'writing'],
    statuses: ['active', 'draft', 'stable'],
    types: ['brief', 'hub', 'note']
  };
}

export function createDemoVaultEvents(now = Date.now()): VaultEvent[] {
  const paths = [
    '20_Projects/Vault Kosmos.md',
    '40_Writing/Public Alpha Story.md',
    '00_Atlas/Routes and Tours.md',
    '30_Sources/WebGL Performance.md',
    '10_Research/Spatial Computing.md',
    '50_People/Future Contributors.md',
    '20_Projects/Presentation Route Alpha.md',
    '40_Writing/Demo Walkthrough.md'
  ];

  return paths.map((path, index) => ({
    id: `demo-event:${index}`,
    type: index % 3 === 0 ? 'add' : 'change',
    path,
    area: path.split('/')[0] ?? 'Demo',
    extension: 'md',
    at: new Date(now - (paths.length - index) * 1000 * 54).toISOString(),
    message: index === paths.length - 1 ? 'Demo focus pulse' : undefined
  }));
}

export function createTrailerVaultGraph(now = Date.now()): VaultGraph {
  const nodes: VaultNode[] = [];
  const links: VaultLink[] = [];

  nodes.push({
    id: 'folder:.',
    kind: 'folder',
    path: '',
    label: 'Vault Kosmos Trailer Vault',
    area: 'Vault',
    depth: 0,
    tags: [],
    aliases: [],
    color: '#e2e8f0',
    outgoing: 0,
    incoming: 0
  });

  for (const area of trailerAreas) {
    nodes.push({
      id: `folder:${area.path}`,
      kind: 'folder',
      path: area.path,
      label: area.label,
      area: area.path,
      depth: 1,
      tags: area.tags,
      aliases: [],
      color: area.color,
      outgoing: 0,
      incoming: 0
    });
    links.push({
      id: `contains:folder:.->folder:${area.path}`,
      source: 'folder:.',
      target: `folder:${area.path}`,
      kind: 'contains'
    });
  }

  let fileIndex = 0;
  const fileIdsByArea = new Map<string, string[]>();
  const allFileIds: string[] = [];

  for (const area of trailerAreas) {
    const areaFileIds: string[] = [];
    for (const note of area.notes) {
      const path = `${area.path}/${note}.md`;
      const id = `file:${path}`;
      const createdAt = new Date(now - (trailerAreas.length * 7 - fileIndex) * 1000 * 60 * 60 * 22).toISOString();
      const updatedAt = new Date(now - ((fileIndex % 8) + 1) * 1000 * 60 * 11).toISOString();
      nodes.push({
        id,
        kind: 'file',
        path,
        label: note,
        area: area.path,
        depth: 2,
        extension: 'md',
        size: 2200 + fileIndex * 163,
        createdAt,
        updatedAt,
        type: fileIndex % 4 === 0 ? 'hub' : fileIndex % 3 === 0 ? 'tour' : 'note',
        status: fileIndex % 5 === 0 ? 'active' : fileIndex % 4 === 0 ? 'draft' : 'stable',
        priority: fileIndex % 6 === 0 ? 'high' : 'normal',
        tags: [...area.tags, 'showcase', fileIndex % 2 === 0 ? 'scripted' : 'linked'],
        aliases: [],
        color: area.color,
        outgoing: 0,
        incoming: 0
      });
      links.push({
        id: `contains:folder:${area.path}->${id}`,
        source: `folder:${area.path}`,
        target: id,
        kind: 'contains'
      });
      areaFileIds.push(id);
      allFileIds.push(id);
      fileIndex += 1;
    }
    fileIdsByArea.set(area.path, areaFileIds);
  }

  for (const target of trailerUnresolvedTargets) {
    nodes.push({
      id: `unresolved:${target}`,
      kind: 'unresolved',
      path: target,
      label: target,
      area: 'Unresolved',
      depth: 1,
      tags: ['future'],
      aliases: [],
      color: '#64748b',
      outgoing: 0,
      incoming: 0,
      unresolved: true
    });
  }

  const trailerRoute = [
    'file:00_Trailer/Your Vault Becomes Visible.md',
    'file:10_Structure/Clusters And Links.md',
    'file:20_Live_Work/Recent Changes Pulse.md',
    'file:30_Focus/Focus Flights.md',
    'file:40_Privacy/Private Vaults Stay Local.md',
    'file:50_Demo/Explore The Static Demo.md'
  ];

  trailerRoute.forEach((source, index) => {
    addLink(links, 'markdown', source, trailerRoute[(index + 1) % trailerRoute.length], 'trailer route');
    addLink(links, 'property', source, 'file:00_Trailer/Public Demo Script.md', 'script');
  });

  for (const [areaIndex, area] of trailerAreas.entries()) {
    const ids = fileIdsByArea.get(area.path) ?? [];
    const nextArea = trailerAreas[(areaIndex + 1) % trailerAreas.length];
    const nextIds = fileIdsByArea.get(nextArea.path) ?? [];
    const routeHub = trailerRoute[Math.min(areaIndex, trailerRoute.length - 1)];

    ids.forEach((id, index) => {
      addLink(links, 'wikilink', id, ids[(index + 1) % ids.length]);
      addLink(links, 'wikilink', id, nextIds[index % nextIds.length]);
      if (id !== routeHub) addLink(links, 'property', id, routeHub, 'scene');
      if (index === 2) addLink(links, 'wikilink', id, `unresolved:${trailerUnresolvedTargets[areaIndex % trailerUnresolvedTargets.length]}`);
    });
  }

  applyDemoCounts(nodes, links);

  const contentLinks = links.filter((link) => link.kind !== 'contains');
  const tags = new Set(trailerAreas.flatMap((area) => area.tags));
  tags.add('showcase');
  tags.add('scripted');
  tags.add('linked');
  tags.add('future');

  return {
    nodes,
    links,
    stats: {
      indexedAt: new Date(now).toISOString(),
      durationMs: 38,
      files: allFileIds.length,
      folders: trailerAreas.length + 1,
      unresolved: trailerUnresolvedTargets.length,
      links: links.length,
      wikilinks: links.filter((link) => link.kind === 'wikilink').length,
      markdownLinks: links.filter((link) => link.kind === 'markdown').length,
      propertyLinks: links.filter((link) => link.kind === 'property').length,
      orphans: nodes.filter(
        (node) =>
          node.kind === 'file' &&
          !contentLinks.some((link) => link.source === node.id || link.target === node.id)
      ).length
    },
    areas: ['Vault', ...trailerAreas.map((area) => area.path), 'Unresolved'],
    tags: [...tags].sort((a, b) => a.localeCompare(b, 'en')),
    statuses: ['active', 'draft', 'stable'],
    types: ['hub', 'note', 'tour']
  };
}

export function createTrailerVaultEvents(now = Date.now()): VaultEvent[] {
  const paths = [
    '00_Trailer/Your Vault Becomes Visible.md',
    '10_Structure/Clusters And Links.md',
    '20_Live_Work/Recent Changes Pulse.md',
    '30_Focus/Focus Flights.md',
    '40_Privacy/Private Vaults Stay Local.md',
    '50_Demo/Explore The Static Demo.md'
  ];

  return paths.map((path, index) => ({
    id: `trailer-event:${index}`,
    type: index === 0 ? 'add' : index % 2 === 0 ? 'change' : 'add',
    path,
    area: path.split('/')[0] ?? 'Trailer',
    extension: 'md',
    at: new Date(now - (paths.length - index) * 1000 * 62).toISOString(),
    message: index === paths.length - 1 ? 'Public demo ready' : 'Trailer route pulse'
  }));
}

function addLink(
  links: VaultLink[],
  kind: VaultLink['kind'],
  source: string,
  target: string,
  label?: string
): void {
  if (!source || !target || source === target) return;
  links.push({
    id: `${kind}:${source}->${target}:${links.length}`,
    source,
    target,
    kind,
    label
  });
}

function applyDemoCounts(nodes: VaultNode[], links: VaultLink[]): void {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  for (const link of links) {
    if (link.kind === 'contains') continue;
    const source = byId.get(link.source);
    const target = byId.get(link.target);
    if (source) source.outgoing += 1;
    if (target) target.incoming += 1;
  }
}
